import {
  Box,
  Button,
  Divider,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { Card, LoadingOverlay } from "@components/ui-kit";
import { Trans } from "@lingui/macro";
import type { SortOrder as GraphqlSortOrder } from "@src/__generated__/graphql";
import {
  TimeTrackingProjectReportOrderByColumnEnum,
  SortOrder as UrqlSortOrder,
} from "@src/__generated__/urql-graphql";
import {
  createTimeReportColumns,
  createTimeReportFooterTotals,
  type TimeReportTableRow,
} from "@src/components/modules/projects/time-report/columns";
import {
  transformBreakdownToRows,
  transformReportsToRows,
} from "@src/components/modules/projects/time-report/transformers";
import DataTable from "@src/components/ui-kit/DataTable/DataTable";
import { NavigationProject } from "@src/components/widgets/AppSubNav";
import { appStore } from "@src/stores/AppStore";
import { currency } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useContext, useMemo } from "react";
import { Filters } from "./Filters";
import { PageProjectTimeReportCtx } from "./PageProjectTimeReport";
import type { Store } from "./Store";

export const formatCurrencyForTimeReport = (amount = 0) => {
  return currency.format(
    amount * 10,
    appStore.workspaceStore.settings?.currency.code ?? "",
    3,
  );
};

const TabTableNavigation = observer(function TabTableNavigation({
  store,
}: {
  store: Store;
}) {
  return (
    <Menu>
      <MenuButton as={Button} colorScheme="grey" variant="outline">
        <Trans>Group by:</Trans>&nbsp;{store.activeOrderByColumnLabel}
      </MenuButton>
      <MenuList>
        <MenuItem
          onClick={() => {
            store.handleOrderChange({
              column: TimeTrackingProjectReportOrderByColumnEnum.UserFullName,
              order: UrqlSortOrder.Desc,
            });
          }}
        >
          <Trans>Users</Trans>
        </MenuItem>
        <MenuItem
          onClick={() => {
            store.handleOrderChange({
              column: TimeTrackingProjectReportOrderByColumnEnum.WorkType,
              order: UrqlSortOrder.Desc,
            });
          }}
        >
          <Trans>Positions</Trans>
        </MenuItem>
        <MenuItem
          onClick={() => {
            store.handleOrderChange({
              column:
                TimeTrackingProjectReportOrderByColumnEnum.OurWorkBudgetItemTitle,
              order: UrqlSortOrder.Desc,
            });
          }}
        >
          <Trans>Budget items</Trans>
        </MenuItem>
        <MenuItem onClick={store.handleMonthlyBreakdownSelect}>
          <Trans>Time breakdown</Trans>
        </MenuItem>
      </MenuList>
    </Menu>
  );
});

type CardReportProps = {
  isLoading: boolean;
};

export const CardReport = observer(function CardReport({
  isLoading,
}: CardReportProps) {
  const { workspaceStore } = useStore();
  const store = useContext(PageProjectTimeReportCtx);

  const reportRows = useMemo(() => {
    if (store.orderBy.clauses.length === 0) return [];
    const column = store.orderBy.clauses[0].column;
    return transformReportsToRows(store.reports, column);
  }, [store.reports, store.orderBy.clauses]);

  const breakdownRows = useMemo(() => {
    return transformBreakdownToRows(store.breakdownGroups);
  }, [store.breakdownGroups]);

  const footerTotals = useMemo(() => {
    return createTimeReportFooterTotals({
      isBreakdown: store.monthlyBreakdownSelected,
      reportTotalAmount: store.reportTotalAmount,
      reportTotalProfitability: store.reportTotalProfitability,
      breakdownTotalTrackedTime: store.breakdownTotalTrackedTime,
      breakdownTotalAmount: store.breakdownTotalAmount,
      breakdownTotalBudget: store.breakdownTotalBudget,
      breakdownTotalPersonalExpense: store.breakdownTotalPersonalExpense,
      breakdownTotalProfit: store.breakdownTotalProfit,
      reportRows: store.monthlyBreakdownSelected ? [] : reportRows,
      getUrl: store.monthlyBreakdownSelected ? undefined : store.getUrl,
    });
  }, [
    reportRows,
    store.monthlyBreakdownSelected,
    store.reportTotalAmount,
    store.reportTotalProfitability,
    store.breakdownTotalTrackedTime,
    store.breakdownTotalAmount,
    store.breakdownTotalBudget,
    store.breakdownTotalPersonalExpense,
    store.breakdownTotalProfit,
    store.getUrl,
  ]);

  const activeRows = store.monthlyBreakdownSelected
    ? breakdownRows
    : reportRows;

  const tableData = useMemo<TimeReportTableRow[]>(
    () =>
      activeRows.map((group) => ({
        id: group.id,
        rowType: "parent",
        group,
        __subRows: (group.__subRows ?? []).map((subRow) => ({
          id: subRow.id,
          rowType: "subRow",
          group,
          subRow,
        })),
      })),
    [activeRows],
  );

  const columns = useMemo(() => {
    const baseColumns = createTimeReportColumns({
      isBreakdown: store.monthlyBreakdownSelected,
      workspaceStore,
      getUrl: store.getUrl,
      fetchMonthlyBreakdown: store.fetchMonthlyBreakdown,
    });

    return baseColumns.map((column) => ({
      ...column,
      footer:
        typeof column.id === "string" ? footerTotals[column.id] : undefined,
    }));
  }, [
    footerTotals,
    store.fetchMonthlyBreakdown,
    store.getUrl,
    store.monthlyBreakdownSelected,
    workspaceStore,
  ]);

  const orderedBy = useMemo(() => {
    if (!store.activeOrderByColumn) {
      return undefined;
    }

    return {
      column: store.activeOrderByColumn.column,
      order: store.activeOrderByColumn.order as GraphqlSortOrder,
    };
  }, [store.activeOrderByColumn]);

  return (
    <Card>
      {store.projectId && (
        <Box px="4" borderBottomWidth="thin" borderBottomColor="grey.100">
          <NavigationProject id={store.projectId} />
        </Box>
      )}

      <HStack
        p="4"
        divider={<Divider h="6" orientation="vertical" />}
        spacing="4"
      >
        <TabTableNavigation store={store} />
        <Filters />
      </HStack>
      <Box borderBottomWidth="thin" borderBottomColor="grey.100" />

      <LoadingOverlay
        isLoading={isLoading}
        position="absolute"
        transparent={false}
      >
        <DataTable.Body<
          TimeReportTableRow,
          TimeTrackingProjectReportOrderByColumnEnum
        >
          tableKey="project-time-report"
          data={tableData}
          columns={columns}
          orderedBy={orderedBy}
          onOrderByChange={(clause) => {
            store.handleOrderChange({
              column: clause.column,
              order: clause.order as UrqlSortOrder | undefined,
            });
          }}
        />
      </LoadingOverlay>
    </Card>
  );
});
